import { addDDAction, addDDError } from './datadog';
import { getTrackerInstance } from './titan';
import { getEventCounter, getOS, setEventCounter } from './event-helper';
import { titanAnalyticsDefaultValues } from './titan-helper';
import { getPageInfo, getPreviousPageInfo } from './path-helper';
import { authStore } from '@wwe-portal/web/providers/stores/auth-store';

const getCorrectItemName = (name: string | null) => {
  if (!name) return null;
  const webCoinPattern = /(^web coins?$|^webcoins?$)/i;
  const rubyPattern = /^rub(y|ies)$/i;

  if (rubyPattern.test(name)) return 'ruby';
  if (webCoinPattern.test(name)) return 'web_coin';

  return name;
};

interface TitanSysGtiEventParameter {
  reference_subtype: string | null;
  reference_subtype_2: string | null;
  reference_subtype_3: string | null;
  transaction_type: string | null;
  transaction_subtype: string | null;
  transaction_promo_id: string | null;
  reference_memo: string | null;
  reference_event_id: string | null;
  item_id: string | null;
  item_name: string | null;
  item_quantity: number | null;
  item_balance: number | null;
  transaction_id: string | null;
  currentUser: unknown;
}

export const titanSendSysGtiEvent = async ({
  reference_subtype = null,
  reference_subtype_2 = null,
  reference_subtype_3 = null,
  transaction_type = null,
  transaction_subtype = null,
  transaction_promo_id = null,
  reference_memo = null,
  reference_event_id = null,
  item_id = null,
  item_name = null,
  item_quantity = null,
  item_balance = null,
  transaction_id = null,
  currentUser = {},
}: Partial<TitanSysGtiEventParameter>) => {
  try {
    const Titan = getTrackerInstance().Titan;
    const defaultValues = await titanAnalyticsDefaultValues(currentUser);
    const parameters = {
      reference_subtype,
      reference_subtype_2,
      reference_subtype_3: reference_subtype_3 || reference_subtype,
      transaction_type,
      transaction_subtype,
      transaction_promo_id,
      reference_memo,
      reference_event_id,
      item_id,
      item_name: getCorrectItemName(item_name),
      item_quantity,
      item_balance,
      transaction_id,
      ...defaultValues,
    };
    Titan.SendEvent({
      name: 'sys.gti',
      parameters,
    });
    addDDAction('sys.gti', parameters);
  } catch (e) {
    addDDError(e);
  }
};

export interface SysPaymentParameter {
  success: boolean;
  error: string | boolean | undefined;
  context: unknown;
  tenant: unknown;
  amount_us: unknown;
  amount_local: unknown;
  local_currency_type: unknown;
  special: unknown;
  special_id: unknown;
  store_sku: unknown;
  game_sku: unknown;
  payment_id: unknown;
  transaction_id: unknown;
  sku_type: unknown;
  is_discounted_package: unknown;
  discount_info: {
    discount_id: unknown;
    original_amount_local: number | undefined;
    original_amount_us: number | undefined;
  };
  section_title: unknown;
  section_id: unknown;
  section_weight: unknown;
  section_color: unknown;
  offer_weight: unknown;
}

export const getDefaultSys = () => {
  const sys_user_id = authStore.getState().currentUser?.userId ?? null;
  const sys_platform = 'web';
  const sys_os_s = getOS();
  return {
    sys_user_id,
    sys_platform,
    sys_os_s,
  };
};
export const getDefaultMeta = () => {
  const pathname = window.location.pathname;
  const pageInfo = getPageInfo(pathname);
  const { sys_user_id, sys_platform, sys_os_s } = getDefaultSys();
  const currentUser = authStore.getState().currentUser;
  return {
    sys_user_id,
    sys_platform,
    sys_os_s,
    reference_id: pageInfo?.pageName ?? null,
    currentUser,
  };
};

export const sendSysPayment = ({
  success = false,
  error = false,
  context = null,
  tenant = null,
  amount_us = null,
  amount_local = null,
  local_currency_type = 'USD',
  special = null,
  special_id = null,
  store_sku = null,
  game_sku = null,
  payment_id = null,
  transaction_id = null,
  sku_type = 'Standard',
  is_discounted_package = null,
  discount_info = {
    discount_id: null,
    original_amount_local: 0,
    original_amount_us: 0,
  },
  section_title = null,
  section_id = null,
  section_weight = null,
  section_color = null,
  offer_weight = null,
}: SysPaymentParameter) => {
  try {
    const Titan = getTrackerInstance().Titan;
    const parameters = {
      success,
      error,
      context,
      tenant,
      amount_us,
      amount_local,
      local_currency_type,
      special,
      special_id,
      store_sku,
      game_sku,
      payment_id,
      transaction_id,
      sku_type,
      is_discounted_package,
      discount_info,
      section_title,
      section_id,
      section_weight,
      section_color,
      offer_weight,
    };
    Titan.SendEvent({
      name: 'sys.payment',
      parameters,
    });
    addDDAction('sys.payment', parameters);
  } catch (e) {
    addDDError(e);
  }
};

export interface SysUIParameter {
  flow_name: unknown;
  interaction_type: unknown;
  interaction_object?: unknown;
  screen_content:
    | {
        name: unknown;
        description: unknown;
        offer_id: unknown;
        suggested_offer_id: unknown;
        transaction_id: unknown;
        asset: unknown;
        button_label: unknown;
        button_url: unknown;
        start_date: unknown;
        end_date: unknown;
      }
    | string;
  interaction_object_category: unknown;
  flow_order: unknown;

  screen_subcategory: unknown;
  screen_type: unknown;

  index?: unknown;
  previous_screen_category?: unknown;
  screen_category?: unknown;
  screen_id?: unknown;
  screen_name?: unknown;
  previous_screen_id?: unknown;
  previous_screen_name?: unknown;
  session?: unknown;
  sys_user_id?: unknown;
  sys_platform?: unknown;
  sys_os_s?: unknown;
}

export const getDefaultSysUIParameter = (flow_name: string | unknown) => {
  const pathname = window.location.pathname;

  const pageInfo = getPageInfo(pathname);
  const previousPageInfo = getPreviousPageInfo();
  const screen_id = pageInfo?.id ?? null;
  const screen_name = pageInfo?.pageName ?? null;
  const screen_category = pageInfo?.pageName ?? null;

  const previous_screen_id = previousPageInfo?.id ?? null;
  const previous_screen_name = previousPageInfo?.pageName ?? null;
  const previous_screen_category = previousPageInfo?.pageName ?? null;

  const session = sessionStorage.getItem('session') ?? null;

  const { sys_user_id, sys_platform, sys_os_s } = getDefaultSys();
  setEventCounter(flow_name);
  const index = getEventCounter(flow_name);
  return {
    index,
    screen_id,
    screen_name,
    screen_category,
    previous_screen_id,
    previous_screen_name,
    previous_screen_category,
    session,
    sys_user_id,
    sys_platform,
    sys_os_s,
  };
};

export const sendCommonTitanEvent = (
  name: string,
  parameters: object | undefined
) => {
  try {
    const Titan = getTrackerInstance().Titan;
    Titan.SendEvent({
      name,
      parameters: parameters,
    });
    addDDAction(name, parameters);
  } catch (e) {
    addDDError(e);
  }
};

export const sendSysUI = (parameters: SysUIParameter) => {
  try {
    const Titan = getTrackerInstance().Titan;
    const defaultSysUIParameter = getDefaultSysUIParameter(
      parameters?.flow_name || 'unknown_telemetric'
    );
    const finalParameters = {
      ...defaultSysUIParameter,
      ...parameters,
      interaction_object:
        parameters?.interaction_object ?? defaultSysUIParameter.screen_name,
      screen_content: parameters?.screen_content || null,
    };
    Titan.SendEvent({
      name: 'sys.ui',
      parameters: finalParameters,
    });
    addDDAction('sys.ui', finalParameters);
  } catch (e) {
    addDDError(e);
  }
};

export interface SysTransactionParameter {
  trans_desc: string | null;
  trans_type: string | null;
  // OffersID
  context: string | null;
  item_balance: number | null | undefined;
  item_id: string | null;
  item_name: string | null;
  quantity: number | null;
  transaction_id: string | null;
  event_source: string | null;
  sys_user_id: string | null;
  sys_platform: string | null;
  sys_os_s: string | null;
  // PageName
  reference_id: string | null;

  reference_subtype_3: string | null;
  transaction_type: string | null;
  currentUser: unknown;
  player_name?: string | null;
  reference_event_id?: string | null;
}

export const sendGameTransactionEvent = ({
  trans_desc,
  trans_type,
  context,
  item_balance,
  item_id,
  item_name,
  quantity,
  transaction_id,
  event_source,
  sys_user_id,
  sys_platform,
  sys_os_s,
  reference_id,
  transaction_type,
  reference_subtype_3,
  currentUser,
  reference_event_id,
}: SysTransactionParameter) => {
  try {
    const Titan = getTrackerInstance().Titan;
    const parameters = {
      trans_desc,
      trans_type,
      context,
      item_id,
      item_name,
      quantity,
      transaction_id,
      event_source,
      sys_user_id,
      sys_platform,
      sys_os_s,
      reference_id,
      item_balance,
    };
    Titan.SendEvent({
      name: 'sys.game_transaction',
      parameters,
    });
    addDDAction('sys.game_transaction', parameters);

    titanSendSysGtiEvent({
      reference_subtype: reference_id,
      reference_subtype_2: context,
      reference_subtype_3,
      transaction_type,
      transaction_subtype: trans_desc,
      transaction_promo_id: context,
      reference_memo: `${trans_type}/${trans_desc}`,
      item_id,
      item_name,
      item_quantity: quantity,
      transaction_id,
      currentUser,
      item_balance,
      reference_event_id,
    });
  } catch (e) {
    addDDError(e);
  }
};
